import { useEffect } from 'react';
import { DataGrid as MuiDataGrid, DataGridProps, GridColDef, GridValidRowModel } from '@mui/x-data-grid';
import { AxiosGETRequest, KeyValuePair, Pagination } from '@myme/utils';
import { usePagination, usePalette } from 'src/hooks';
import { FlexColumn } from 'src/components/layout';
import { CheckboxCell } from './CheckboxCell';
import { DataGridHeader } from './DataGridHeader';
import { useFilters } from './useFilters';
import { QueryStringValues, useLoadRows } from './useLoadRows';
import { useSorting } from './useSorting';

interface CustomProps {
  query: AxiosGETRequest;
  columns: GridColDef[];
  filterColumns: GridColDef[];
  title?: string;
  queryArgs?: KeyValuePair[];
  queryStringParams?: KeyValuePair[];
  defaultPagination?: Pagination;
  overriddenRows?: GridValidRowModel[]; // used when backend endpoint is not yet available
}

type MuiDataGridProps = Omit<DataGridProps, 'rows'>;
type Props = MuiDataGridProps & CustomProps;

const defaultRowsPerPageOptions = [3, 5, 10, 15];

export const DataGrid = ({
  query,
  queryArgs,
  queryStringParams,
  columns,
  filterColumns,
  rowsPerPageOptions = defaultRowsPerPageOptions,
  title,
  defaultPagination,
  overriddenRows,
  autoHeight,
  sx,
  ...props
}: Props) => {
  const { grey } = usePalette();
  const { pagination, handleChangePage, handleChangePageSize } = usePagination(defaultPagination);
  const { filter, ...applyFilters } = useFilters();
  const { sorting, applySorting } = useSorting();

  useEffect(() => {
    handleChangePage(0);
  }, [filter]);

  const queryStringValues: QueryStringValues = { pagination, filter, sorting };
  const rows = useLoadRows(query, queryStringValues, { args: queryArgs, params: queryStringParams });

  return (
    <FlexColumn>
      <DataGridHeader title={title} filterColumns={filterColumns} filterValue={filter} {...applyFilters} />
      <div style={{ height: !autoHeight ? getGridHeight(pagination.pageSize) : 3000 }}>
        <MuiDataGrid
          autoHeight={false}
          rowCount={rows.data?.total || 0}
          rows={overriddenRows || rows.data?.rows || []}
          columns={columns}
          loading={rows.isLoading}
          isCellEditable={() => false}
          sortingMode='server'
          onSortModelChange={applySorting}
          filterMode='server'
          rowsPerPageOptions={rowsPerPageOptions}
          paginationMode='server'
          page={pagination.page}
          pageSize={pagination.pageSize}
          onPageChange={handleChangePage}
          onPageSizeChange={handleChangePageSize}
          components={{ BaseCheckbox: CheckboxCell }}
          disableSelectionOnClick
          disableColumnMenu
          rowHeight={80}
          sx={{
            border: '0px',
            '& .MuiDataGrid-cell': {
              borderBottom: '0.5px solid grey',
              borderColor: grey[500]
            },
            '& .MuiDataGrid-columnHeaders': {
              borderBottom: '0.5px solid grey',
              borderColor: grey[500]
            },
            '& .Mui-resizeTriggers': {
              border: '0px'
            },
            '& .MuiDataGrid-footerContainer': {
              border: '0px'
            },
            ...sx
          }}
          {...props}
        />
      </div>
    </FlexColumn>
  );
};

const getGridHeight = (rowsPerPage: number) => {
  switch (rowsPerPage) {
    case 15: {
      return 1360;
    }
    case 10: {
      return 960;
    }
    case 3:
    case 5:
    default: {
      return 600;
    }
  }
};
